import { inject, Injectable } from '@angular/core';
import { MeetingSummaryResult } from '../../models/nova-voice.model';
import { AgentCategory } from '../models/agent-config.model';
import { AiClientService } from '../services/ai-client.service';
import { AiConversation, AiConversationItem, TurnMeta } from '../transports/ai-transport';
import { MeetingDataService } from '@omni/data-services/meeting/meeting.data.service';
import { AppointmentActivity } from '@omni/classes/activity/appointment.activity.class';
import { SummaryConversationCacheService } from '../services/summary-conversation-cache.service';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { peelMeetingAnalysis, peelTranscript } from './summary-item-peel';

/**
 * Post-meeting transcript analysis against the `MeetingSummary` agent.
 *
 * Lifecycle:
 *  - `startConversation` runs at screen launch. It implicitly restores any prior
 *    analysis (local cache first, then Foundry history via the stored id) and, if
 *    a conversation id exists, resumes it so the handle stays live for the assistant.
 *    Returns the restored summary, or null (keeping the meeting set) → fresh screen.
 *  - `analyze` runs at "Stop & analyze": it discards the existing conversation (if
 *    any), creates a new one, analyzes, persists the new id, and caches the result.
 *  - `endConversation` simply releases the handle (no server-side delete).
 */
@Injectable({ providedIn: 'root' })
export class MeetingSummaryFeature {
  #conversation: AiConversation | null = null;
  /** Meeting in scope, established at screen launch by startConversation(). */
  #meeting: AppointmentActivity | null = null;
  #transcript: string | null = null;
  readonly #ai = inject(AiClientService);
  readonly #meetingDataService = inject(MeetingDataService);
  readonly #cache = inject(SummaryConversationCacheService);

  isAvailable(): boolean {
    return this.#ai.isAvailable(AgentCategory.MeetingSummary);
  }

  /**
   * Screen launch. Implicitly restores any prior analysis — local cache first
   * (instant, offline-friendly), then Foundry history. If a conversation id is on
   * the record it is resumed so the handle stays live. Returns the restored summary,
   * or null while keeping the meeting set (→ fresh start screen).
   */
  async startConversation(meeting: AppointmentActivity): Promise<MeetingSummaryResult | null> {
    this.#meeting = meeting;
    this.#conversation = null;
    this.#transcript = null;

    // Resume the stored conversation (if any). Cheap local handle — no network call yet.
    const existingId = await this.#meetingDataService.resolveMeetingConversationId(meeting);
    if (existingId) {
      this.#conversation = await this.#ai.resumeConversation(AgentCategory.MeetingSummary, existingId);
    }

    // Restore via the cache ladder: local items → Foundry (by conv id) + cache → none.
    const items = await this.#cache.resolve(AgentCategory.MeetingSummary, existingId, this.#cacheKey(meeting.ID));
    return items ? peelMeetingAnalysis(items) : null;
  }

  /**
   * "Stop & analyze" for the meeting in scope (established at launch): discard the
   * existing conversation (if any), create a fresh one, analyze the transcript,
   * then persist the new id onto the meeting record and cache the result locally.
   */
  async analyze(transcript: string): Promise<MeetingSummaryResult> {
    const meeting = this.#meeting;
    if (!meeting) {
      throw new Error('No meeting in scope; call startConversation() first.');
    }
    // The existing conversation (if any) was already resolved + resumed at launch
    // and is held in #conversation — no need to resolve the id again here.
    const previous = this.#conversation;

    const conv = await this.#ai.startConversation(AgentCategory.MeetingSummary);
    const { AnalysisSchema } = await import('./meeting-summary.schema');
    // recordId scopes the grounding to this meeting (recordType 'appointment' → the meeting device
    // sources); the transport also attaches the rep's session/identity context for impersonation.
    conv.setStructuredInputs({ recordId: meeting.ID, recordType: 'appointment', origin: 'goapp-transcript' });
    const summary = await conv.parse<MeetingSummaryResult>(
      { transcript },
      AnalysisSchema,
      'analysis',
    );

    this.#conversation = conv;
    // Save the conversation id to Dynamics in the BACKGROUND — don't block the
    // analysis result on a network PATCH. persist() sets activity.conversationId
    // synchronously before its first await, so local resume works immediately;
    // only the remote PATCH is deferred. Chain any dependent work off .then().
    void this.#meetingDataService.persistMeetingConversationId(meeting, conv.id)
      .catch(e => console.error('Persist conversation id failed:', e));
    // Cache the transcript + analysis turns for offline / cross-system reopen.
    // Build the two items in-memory from what we already have — parse() commits
    // through the Responses API while the Conversations API has no read-after-write
    // guarantee, so an immediate fetch could race and cache nothing. Peelers match
    // by shape, not order.
    try {
      const items: AiConversationItem[] = [
        { id: '', role: 'user', text: JSON.stringify({ transcript }) },
        { id: '', role: 'assistant', text: JSON.stringify(summary) },
      ];
      await this.#cache.cacheItems(this.#cacheKey(meeting.ID), conv.id, items);
    } catch (e) {
      console.error('Cache meeting conversation failed:', e);
    }

    this.#transcript = transcript;
    if (previous && previous.id !== conv.id) {
      await previous.delete().catch(() => undefined); // discard the superseded analysis
    }
    return summary;
  }

  /**
   * Turn metadata for the most recent `analyze()` — what the feedback footer rates.
   * Null on an analysis restored from cache or history: no turn was made in this
   * session, so there is no response to attach a vote to.
   */
  get lastTurn(): TurnMeta | null {
    return this.#conversation?.lastTurn ?? null;
  }

  async getTranscript(): Promise<string | null> {
    if (this.#transcript) return this.#transcript;
    if (!this.#meeting) return null;
    try {
      const existingId = await this.#meetingDataService.resolveMeetingConversationId(this.#meeting);
      const items = await this.#cache.resolve(
        AgentCategory.MeetingSummary, existingId, this.#cacheKey(this.#meeting.ID),
      );
      if (items) this.#transcript = peelTranscript(items);
    } catch (e) {
      console.warn('getTranscript: could not resolve transcript:', e);
    }
    return this.#transcript;
  }

  /**
   * Releases the local conversation handle WITHOUT deleting it server-side, so
   * the meeting's persisted id stays resumable. Call when dismissing the modal.
   */
  async endConversation(): Promise<void> {
    this.#conversation = null;
    this.#transcript = null;
  }

  /** Disk cache key for this meeting's conversation items. */
  #cacheKey(meetingId: string): string {
    return DB_KEY_PREFIXES.NOVA_ANALYSIS + meetingId;
  }

}
